// HOME LAYOUT v2 — the workspace "/" board as an ORDER of widgets, each with
// one of four sizes (HOME_WIDGETS_SPEC.md §1 + §6). No free placement: the
// CSS grid flows the order (`grid-auto-flow: row dense`), so a hidden or
// removed widget can never leave a hole and nothing has coordinates to drift.
//
// WIRE SHAPE — the same prefs field the 12-column GridStack map used
// (`grid_layout`: Mixed, round-trips byte-identically; the server's tenancy
// pass only strips foreign ids while walking arrays of `{ id }`), as an
// ORDERED ARRAY of `{ id, size, v: 2 }`. An array, not a wrapper object, so
// that tenancy pass still sees every id.
//
// A 12-column record converts ONCE on read: reading order (y, then x) and
// w × h snapped to the nearest size by `homeWidgetSizeForCell` — the same
// mapping the interim board used, so a converted board keeps the look its
// owner arranged.
//
// Pure functions, never mutate their input; pages/main.js keeps the state,
// the cache and the durable writes.
import { HOME_WIDGET_SIZES, homeWidgetSizeForCell } from './homeGridMetrics.js'

const _RANK = { s: 0, m: 1, l: 2, xl: 3 }

const _size = (s) => (HOME_WIDGET_SIZES.includes(s) ? s : 's')

const _isEntry = (e) => !!e && typeof e === 'object' && typeof e.id === 'string' && e.id !== ''

export const homeLayoutIsV2 = (layout) =>
  Array.isArray(layout) && layout.some((e) => _isEntry(e) && e.v === 2)

export const homeLayoutFromLegacy = (layout) => {
  if (!Array.isArray(layout)) return []
  const rows = []
  layout.forEach((e, i) => {
    if (!_isEntry(e)) return
    const y =
      Number.isFinite(Number(e.y)) && e.y !== null && e.y !== undefined ? Number(e.y) : Infinity
    const x =
      Number.isFinite(Number(e.x)) && e.x !== null && e.x !== undefined ? Number(e.x) : Infinity
    rows.push({ e, i, y, x })
  })
  rows.sort((a, b) => a.y - b.y || a.x - b.x || a.i - b.i)
  const seen = new Set()
  const out = []
  for (const { e } of rows) {
    if (seen.has(e.id)) continue
    seen.add(e.id)
    out.push({ id: e.id, size: homeWidgetSizeForCell(e.w, e.h), v: 2 })
  }
  return out
}

export const homeLayoutNormalize = (layout) => {
  if (!Array.isArray(layout) || !layout.length) return []
  if (!homeLayoutIsV2(layout)) return homeLayoutFromLegacy(layout)
  const seen = new Set()
  const out = []
  for (const e of layout) {
    if (!_isEntry(e) || seen.has(e.id)) continue
    seen.add(e.id)
    out.push({ id: e.id, size: _size(e.size), v: 2 })
  }
  return out
}

// Append every widget of `entries` (`[{ id, size? }]`, the default order) the
// layout does not list yet. The saved order is never touched. Returns the
// SAME array when nothing is missing, so a caller can skip a no-op write.
export const homeLayoutEnsure = (layout, entries) => {
  const base = Array.isArray(layout) ? layout : []
  const have = new Set(base.map((e) => e && e.id))
  const add = []
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!_isEntry(e) || have.has(e.id)) continue
    have.add(e.id)
    add.push({ id: e.id, size: _size(e.size), v: 2 })
  }
  return add.length ? [...base, ...add] : layout
}

// The workspace's own panel list orders the DEFAULT board
// (HOME-DEFAULT-PANELS-ORDER-1, decided 2026-10-04): the ids the list names
// come first, in the list's order, then every other entry in its own order.
// A listed id the board does not have is skipped. Only the default board reads
// this — a member's saved layout keeps its own order (homeLayoutEnsure only
// appends to it). Returns the SAME array when nothing is listed.
export const homeLayoutOrderBy = (entries, ids) => {
  const list = Array.isArray(entries) ? entries : []
  const order = Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id) : []
  if (!order.length || !list.length) return entries
  const byId = new Map()
  for (const e of list) if (_isEntry(e) && !byId.has(e.id)) byId.set(e.id, e)
  const head = []
  const taken = new Set()
  for (const id of order) {
    const e = byId.get(id)
    if (!e || taken.has(id)) continue
    taken.add(id)
    head.push(e)
  }
  if (!head.length) return entries
  return [...head, ...list.filter((e) => !(_isEntry(e) && taken.has(e.id)))]
}

// Move one widget to `toIndex` (clamped) in the flow.
export const homeLayoutMove = (layout, id, toIndex) => {
  const list = Array.isArray(layout) ? layout : []
  const from = list.findIndex((e) => e && e.id === id)
  if (from < 0) return layout
  const next = list.slice()
  const [item] = next.splice(from, 1)
  const to = Math.max(0, Math.min(next.length, Math.floor(Number(toIndex)) || 0))
  next.splice(to, 0, item)
  return next
}

// The board's id for a card the assistant names. An installed app's card is
// listed under its bare widget id (`val-skip-watch`), while the assistant —
// reading the picked card's chip, "card val-skip-watch · widget
// storm-validators.val-skip-watch" — may name it by the app-qualified ref
// (dev 2026-10-04: `replaces: 'storm-validators.val-skip-watch'` left the old
// card on the board). The exact id wins; else the part after the app prefix,
// only when the board lists that bare id. Returns null for an unknown card.
export const homeLayoutCardId = (layout, ref) => {
  const list = Array.isArray(layout) ? layout : []
  const id = typeof ref === 'string' ? ref.trim() : ''
  if (!id) return null
  if (list.some((e) => e && e.id === id)) return id
  const dot = id.lastIndexOf('.')
  if (dot <= 0 || dot === id.length - 1) return null
  const bare = id.slice(dot + 1)
  return list.some((e) => e && e.id === bare) ? bare : null
}

// A card made by "Change with AI" takes the place of the one it replaces
// (HOME-WIDGET-CHANGE-WITH-AI-1): the new id goes right after the old entry,
// at the old entry's size. The old entry stays listed — the caller hides it —
// so a restore from Customize lands where it was. Returns the SAME array when
// nothing changes (unknown old id, no new id, or the same id).
export const homeLayoutReplace = (layout, oldId, newId) => {
  const list = Array.isArray(layout) ? layout : []
  if (!oldId || !newId || oldId === newId) return layout
  const at = list.findIndex((e) => e && e.id === oldId)
  if (at < 0) return layout
  const size = _size(list[at].size)
  const next = list.filter((e) => !(e && e.id === newId))
  next.splice(next.findIndex((e) => e && e.id === oldId) + 1, 0, { id: newId, size, v: 2 })
  return next
}

// Give one widget a size, never below `minSize` — content sets the smallest
// size a widget renders at (quality law, HOME_AMBIENT_SKIN_SPEC §3). An
// unknown widget is appended with that size.
export const homeLayoutSetSize = (layout, id, size, minSize) => {
  const list = Array.isArray(layout) ? layout : []
  let want = _size(size)
  const min = _size(minSize)
  if (_RANK[want] < _RANK[min]) want = min
  const at = list.findIndex((e) => e && e.id === id)
  if (at < 0) return [...list, { id, size: want, v: 2 }]
  const next = list.slice()
  next[at] = { ...next[at], size: want, v: 2 }
  return next
}

// The size a widget renders at: its saved size, else `fallback`, and never
// below `minSize`.
export const homeLayoutSizeOf = (layout, id, fallback, minSize) => {
  const hit = Array.isArray(layout) ? layout.find((e) => e && e.id === id) : null
  const size = _size(hit ? hit.size : fallback)
  const min = _size(minSize)
  return _RANK[size] < _RANK[min] ? min : size
}

// id → flow index.
export const homeLayoutOrderOf = (layout) => {
  const map = new Map()
  ;(Array.isArray(layout) ? layout : []).forEach((e, i) => {
    if (_isEntry(e) && !map.has(e.id)) map.set(e.id, i)
  })
  return map
}
